import { useCallback, useSyncExternalStore } from "react";

const PREFIX = "desk.";
const listeners = new Map<string, Set<() => void>>();

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function getStored<T>(key: string, fallback: T): T {
  return read(key, fallback);
}

export function setStored<T>(key: string, value: T | undefined) {
  try {
    if (value === undefined) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage full or disabled; keep in-memory behaviour */
  }
  listeners.get(key)?.forEach((l) => l());
}

/** Raw string keys (used by the pre-paint script in index.html). */
export function getRaw(key: string): string | null {
  try {
    return localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}
export function setRaw(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(PREFIX + key);
    else localStorage.setItem(PREFIX + key, value);
  } catch {
    /* ignore */
  }
  listeners.get(key)?.forEach((l) => l());
}

const snapshotCache = new Map<string, { raw: string | null; value: unknown }>();

/** React hook for a JSON value persisted in localStorage, shared across components. */
export function useStored<T>(key: string, fallback: T): [T, (v: T | ((prev: T) => T)) => void] {
  const subscribe = useCallback(
    (cb: () => void) => {
      let set = listeners.get(key);
      if (!set) listeners.set(key, (set = new Set()));
      set.add(cb);
      const onStorage = (e: StorageEvent) => e.key === PREFIX + key && cb();
      window.addEventListener("storage", onStorage);
      return () => {
        set!.delete(cb);
        window.removeEventListener("storage", onStorage);
      };
    },
    [key],
  );
  const getSnapshot = useCallback(() => {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(PREFIX + key);
    } catch {
      raw = null;
    }
    const cached = snapshotCache.get(key);
    if (cached && cached.raw === raw) return cached.value as T;
    let value: T = fallback;
    if (raw !== null) {
      try {
        value = JSON.parse(raw) as T;
      } catch {
        value = fallback;
      }
    }
    snapshotCache.set(key, { raw, value });
    return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback(
    (v: T | ((prev: T) => T)) => {
      const next = typeof v === "function" ? (v as (p: T) => T)(read(key, fallback)) : v;
      setStored(key, next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return [value, setValue];
}

export function deviceId(): string {
  let id = getRaw("deviceId");
  if (!id) {
    id = `web-${crypto.randomUUID().slice(0, 8)}`;
    setRaw("deviceId", id);
  }
  return id;
}

/**
 * Id this client polls the notification schedule with. Electron's native notifier uses the shell's
 * id, so Settings must offer that one as "This Mac".
 */
export function notificationDeviceId(): string {
  return (typeof window !== "undefined" && window.deskBridge?.notificationDevice) || deviceId();
}
